import { useCallback, useEffect, useState } from 'react'
import type { ScheduleEntry } from '@myapp/contract'
import type { PrimaryWorkAction } from './workActionPolicy'
import type { PhaseTaps, TapAction } from './usePhaseTaps'
import { resolveStartGate, startGateFromError, type StartGate } from './startGate'

type OpenGate = Exclude<StartGate, { kind: 'free' }>

export interface StartGateController {
  /**
   * Call before `taps.run`. Returns `true` when the gate took the tap over (a dialog is now open),
   * `false` when the caller should go ahead as usual.
   */
  intercept: (action: PrimaryWorkAction) => boolean
  /** The open dialog, if any. */
  open: OpenGate | null
  /** „Trotzdem starten“ — resends the tap with the confirmation and the optional reason. */
  confirm: (note: string) => void
  close: () => void
}

const isStart = (action: PrimaryWorkAction): action is TapAction =>
  action === 'start-travel' || action === 'start'

/**
 * Starting an item the office is rescheduling (soft block, user decision 05.10.2026).
 *
 * Shared by the appointment and the task page. It sits in front of `usePhaseTaps`: a start on a
 * flagged entry opens a dialog instead of firing, and the confirmed retry carries
 * `confirmRescheduling` plus the worker's reason. A 409 for a flag that arrived after the page
 * loaded opens the same dialog, so the race ends where the normal path does.
 */
export function useStartGate(
  schedule: ScheduleEntry | null | undefined,
  taps: PhaseTaps,
): StartGateController {
  const [open, setOpen] = useState<OpenGate | null>(null)
  const [pending, setPending] = useState<TapAction | null>(null)

  const intercept = useCallback(
    (action: PrimaryWorkAction) => {
      if (!isStart(action)) return false
      // Remembered even when the tap goes through: if the server answers with a gate 409, the
      // confirmed retry has to resend this same action.
      setPending(action)
      const gate = resolveStartGate(schedule)
      if (gate.kind === 'free') return false
      setOpen(gate)
      return true
    },
    [schedule],
  )

  // The server disagreed with the snapshot this page loaded: show what it said.
  const { error } = taps
  useEffect(() => {
    const gate = startGateFromError(error)
    if (gate) setOpen(gate)
  }, [error])

  const close = useCallback(() => setOpen(null), [])

  const confirm = useCallback(
    (note: string) => {
      setOpen(null)
      const trimmed = note.trim()
      taps.run(pending ?? 'start-travel', {
        confirmRescheduling: true,
        ...(trimmed ? { reschedulingNote: trimmed } : {}),
      })
    },
    [taps, pending],
  )

  return { intercept, open, confirm, close }
}
